import type { CollisionShape } from '../geometry';
import type { RegionDefinition, RegionZone, WorldMapDefinition } from '../region-definition';
import type { BiomeId } from '../types';
import { MEADOW_COLLISION, MEADOW_ZONES } from './meadow-map-data';
import { FOREST_COLLISION, FOREST_ZONES } from './forest-map-data';
import { CAVE_COLLISION, CAVE_ZONES } from './cave-map-data';
import { COAST_COLLISION, COAST_ZONES } from './coast-map-data';
import type { ContinuousWorldDefinition } from '../continuous-world';

const REGION_WIDTH = 2560;
const REGION_HEIGHT = 1920;
const WORLD_BOUNDS = { x: 0, y: 0, w: 5120, h: 3840 } as const;

function zonesFor(regionId: BiomeId, x: number, y: number): readonly RegionZone[] {
  if (regionId === 'meadow') return MEADOW_ZONES;
  if (regionId === 'forest') return FOREST_ZONES;
  if (regionId === 'cave') return CAVE_ZONES;
  if (regionId === 'coast') return COAST_ZONES;
  return [
    { id: `${regionId}-entry`, kind: 'entry', area: { x: x + 96, y: y + 96, w: 192, h: 128 } },
    { id: `${regionId}-npc`, kind: 'npc', area: { x: x + 320, y: y + 180, w: 160, h: 128 } },
    {
      id: `${regionId}-resource`,
      kind: 'resource',
      area: { x: x + 520, y: y + 160, w: 160, h: 160 },
    },
    { id: `${regionId}-fauna`, kind: 'fauna', area: { x: x + 720, y: y + 120, w: 480, h: 320 } },
    { id: `${regionId}-wild`, kind: 'wild', area: { x: x + 160, y: y + 440, w: 480, h: 320 } },
    {
      id: `${regionId}-dominant`,
      kind: 'dominant',
      area: { x: x + 680, y: y + 440, w: 360, h: 320 },
    },
    {
      id: `${regionId}-arena`,
      kind: 'arena',
      area: {
        kind: 'polygon',
        id: `${regionId}-arena-area`,
        points: [
          { x: x + 900, y: y + 500 },
          { x: x + 1120, y: y + 500 },
          { x: x + 1010, y: y + 740 },
        ],
      },
    },
    {
      id: `${regionId}-no-spawn`,
      kind: 'no-spawn',
      area: { x: x + 64, y: y + 800, w: 1152, h: 96 },
    },
  ];
}

function region(
  id: BiomeId,
  x: number,
  y: number,
  collision: readonly CollisionShape[],
): RegionDefinition {
  const bounds = { x, y, w: REGION_WIDTH, h: REGION_HEIGHT };
  return {
    id,
    biome: id,
    bounds,
    entrances: [],
    collision,
    zones: zonesFor(id, x, y),
    camera: { bounds: WORLD_BOUNDS, zoom: 2.5 },
    renderer: {
      kind: 'layered-raster',
      manifestUrl: `/assets/generated/world/${id}/${id}-manifest.json`,
    },
  };
}

export const CURRENT_WORLD_MAP: WorldMapDefinition = {
  id: 'current-world',
  width: 5120,
  height: 3840,
  fallbackRegionId: 'meadow',
  regions: [
    region('meadow', 0, 0, MEADOW_COLLISION),
    region('forest', 2560, 0, FOREST_COLLISION),
    region('cave', 0, 1920, CAVE_COLLISION),
    region('coast', 2560, 1920, COAST_COLLISION),
  ],
};

export const CURRENT_CONTINUOUS_WORLD: ContinuousWorldDefinition = {
  id: 'current-world',
  bounds: WORLD_BOUNDS,
  biomeHysteresis: 48,
  biomeZones: [
    { id: 'meadow-biome', biome: 'meadow', area: { x: 0, y: 0, w: 2560, h: 1920 } },
    { id: 'forest-biome', biome: 'forest', area: { x: 2560, y: 0, w: 2560, h: 1920 } },
    { id: 'cave-biome', biome: 'cave', area: { x: 0, y: 1920, w: 2560, h: 1920 } },
    { id: 'coast-biome', biome: 'coast', area: { x: 2560, y: 1920, w: 2560, h: 1920 } },
  ],
  foundationChunks: [],
  props: [],
  terrainBlockers: CURRENT_WORLD_MAP.regions.flatMap((entry) => entry.collision),
  bridgeCorridors: [],
};
